import { EditorCaret, EditorChange, clipboardIsStructured, keyChange, pasteChange } from './descriptionEditorCommands'
import {
  affectedLineIds,
  caretFromSelection,
  caretOffsetIn,
  lineRowFromNode,
  readEditorDom
} from './descriptionEditorDom'
import { runLength } from './mobileDescription'

export const commandLineIds = (root: HTMLElement, fallback: string | null): string[] => {
  const ids = affectedLineIds(root)
  if (ids.length > 0) {
    return ids
  }
  if (fallback === null) {
    return []
  }
  return [fallback]
}

const collapsedForKey = (key: string, collapsed: boolean): boolean => key === 'Enter' || collapsed

export const editForKey = (root: HTMLElement, event: KeyboardEvent): EditorChange | null => {
  if (event.key !== 'Enter' && event.key !== 'Backspace' && event.key !== 'Delete') {
    return null
  }
  const selection = window.getSelection()
  const collapsed = selection === null || selection.isCollapsed
  if (event.key === 'Enter' && !collapsed) {
    event.preventDefault()
    document.execCommand('delete')
  }
  const row = lineRowFromNode(root, window.getSelection()?.anchorNode ?? null)
  const id = row?.dataset.lineId ?? ''
  if (row === null || id === '') {
    return null
  }
  return keyChange({
    lines: readEditorDom(root),
    id,
    key: event.key,
    offset: caretOffsetIn(row),
    collapsed: collapsedForKey(event.key, collapsed)
  })
}

export const caretForRewrite = (root: HTMLElement, lines: ReturnType<typeof readEditorDom>): EditorCaret => {
  const found = caretFromSelection(root)
  if (found !== null && lines.some((line) => line.id === found.id)) {
    return found
  }
  const last = lines[lines.length - 1]
  return { id: last.id, offset: runLength(last.runs) }
}

export interface PasteResult {
  change: EditorChange | null
  sync: boolean
}

export const pasteEditor = (root: HTMLElement, clipboard: string): PasteResult => {
  const selection = window.getSelection()
  if (selection !== null && !selection.isCollapsed) {
    document.execCommand('delete')
  }
  if (!clipboardIsStructured(clipboard)) {
    root.focus()
    document.execCommand('insertText', false, clipboard)
    return { change: null, sync: true }
  }
  const row = lineRowFromNode(root, window.getSelection()?.anchorNode ?? null)
  const id = row?.dataset.lineId ?? ''
  if (row === null || id === '') {
    return { change: null, sync: true }
  }
  return {
    change: pasteChange({ lines: readEditorDom(root), id, offset: caretOffsetIn(row) ?? 0, clipboard }),
    sync: false
  }
}
